import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, QueueQuery } from '@/lib/api'
import toast from 'react-hot-toast'

export interface MongoUserData {
  name: string | null
  email: string | null
  username: string | null
  isVip: boolean
}

export interface MongoUserDataWithDate extends MongoUserData {
  createdAt: string
}

export interface BridgeRequest {
  id: string
  userId: string
  direction: 'UNI2_TO_BSC' | 'BSC_TO_UNI2'
  sourceTxHash: string
  contractLockId: number | null
  sourceBlockNumber: number | null
  amountIn: string
  amountOut: string
  formattedAmountIn: string
  formattedAmountOut: string
  fromAddress: string
  toAddress: string
  feePercentage: number
  feeAmount: string
  queuePosition: number | null
  status: 'PENDING' | 'APPROVED' | 'COMPLETED' | 'CANCELLED' | 'REJECTED' | 'SCHEDULED' | 'FAILED'
  scheduledAt: string | null
  executedAt: string | null
  processedAt: string | null
  processedBy: string | null
  destTxHash: string | null
  rejectionReason: string | null
  cancelledAt: string | null
  createdAt: string
  updatedAt: string
  user: {
    walletAddress: string
  }
  mongoUserData: MongoUserData | null
  allMongoUsers: MongoUserDataWithDate[] | null
  walletAlias: string | null
}

export interface QueueStats {
  pending: number
  approved: number
  scheduled: number
  completed: number
  rejected: number
  cancelled: number
}

export interface QueueResponse {
  requests: BridgeRequest[]
  total: number
  stats: QueueStats
}

export function useAdminQueue(params?: QueueQuery) {
  const queryClient = useQueryClient()

  const { data, isLoading, error, refetch } = useQuery<QueueResponse>({
    queryKey: ['adminQueue', params],
    queryFn: () => api.getAdminQueue(params),
    retry: 1,
    staleTime: 10000,
  })

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.approveRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminQueue'] })
      queryClient.invalidateQueries({ queryKey: ['adminStats'] })
      toast.success('Solicitud aprobada')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al aprobar')
    },
  })

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason, showReasonToUser }: { id: string; reason: string; showReasonToUser?: boolean }) =>
      api.rejectRequest(id, reason, showReasonToUser ?? false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminQueue'] })
      queryClient.invalidateQueries({ queryKey: ['adminStats'] })
      toast.success('Solicitud rechazada')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al rechazar')
    },
  })

  const confirmReleaseMutation = useMutation({
    mutationFn: ({ id, destTxHash }: { id: string; destTxHash: string }) => api.confirmRelease(id, destTxHash),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminQueue'] })
      queryClient.invalidateQueries({ queryKey: ['adminStats'] })
      queryClient.invalidateQueries({ queryKey: ['auditLogs'] })
      toast.success('Envío confirmado')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al confirmar')
    },
  })

  return {
    requests: data?.requests || [],
    total: data?.total || 0,
    stats: data?.stats,
    isLoading,
    error,
    refetch,
    approveRequest: approveMutation.mutate,
    isApproving: approveMutation.isPending,
    rejectRequest: rejectMutation.mutate,
    isRejecting: rejectMutation.isPending,
    confirmRelease: confirmReleaseMutation.mutate,
    isConfirming: confirmReleaseMutation.isPending,
  }
}
